import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, AlertCircle, Bell, CreditCard, UtensilsCrossed, CheckCircle2 } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;

/* ── darken a hex color for button shadow ── */
function darkenHex(hex, amount = 60) {
  const h = hex.replace('#', '');
  const num = parseInt(h, 16);
  const r = Math.max(0, (num >> 16) - amount);
  const g = Math.max(0, ((num >> 8) & 0xff) - amount);
  const b = Math.max(0, (num & 0xff) - amount);
  return `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
}

/* ── is color "light" (needs dark text)? ── */
function isLight(hex) {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) > 160;
}

/* ── Wave rings component ── */
const WaveRings = ({ color }) => (
  <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
    {[0, 1, 2].map((i) => (
      <span
        key={i}
        className="absolute rounded-full opacity-0"
        style={{
          width: '100%',
          height: '100%',
          border: `3px solid ${color}`,
          animation: `waveRing 2s ease-out ${i * 0.6}s infinite`,
        }}
      />
    ))}
  </div>
);

export const CustomerAllocationHome = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [allocation, setAllocation] = useState(null);
  const [property, setProperty] = useState(null);
  const [callingLoading, setCallingLoading] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [toast, setToast] = useState(null);

  /* ─── fetch ─── */
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${BACKEND_URL}/api/device/${deviceId}/data`);
        if (res.data.code === 200 && res.data.data) {
          setAllocation(res.data.data.allocation);
          setProperty(res.data.data.property);
        } else {
          setError(res.data.message || 'No active allocation found for this device.');
        }
      } catch {
        setError('Could not load your details. Please try again.');
      } finally {
        setLoading(false);
      }
    })();
  }, [deviceId]);

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3000);
  };

  const handleCalling = async () => {
    if (callingLoading) return;
    setCallingLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/devices/${deviceId}/toggle-calling`);
      if (res.data.code === 200) {
        const newFlag = res.data.data?.callingFlag;
        setAllocation((p) => ({ ...p, callingFlag: newFlag }));
        showToast(newFlag === 'Non Calling' ? 'Call cancelled.' : 'Staff has been notified!', newFlag === 'Non Calling' ? 'info' : 'success');
      } else showToast('Something went wrong.', 'error');
    } catch { showToast('Could not reach server.', 'error'); }
    finally { setCallingLoading(false); }
  };

  const handleCheckout = async () => {
    if (checkoutLoading) return;
    setCheckoutLoading(true);
    try {
      const res = await axios.post(`${BACKEND_URL}/api/devices/${deviceId}/toggle-checkout`);
      if (res.data.code === 200) {
        const newFlag = res.data.data?.callingFlag;
        setAllocation((p) => ({ ...p, callingFlag: newFlag }));
        showToast(newFlag === 'Non Calling' ? 'Checkout request cancelled.' : 'Staff will assist you shortly.', newFlag === 'Non Calling' ? 'info' : 'success');
      } else showToast('Something went wrong.', 'error');
    } catch { showToast('Could not reach server.', 'error'); }
    finally { setCheckoutLoading(false); }
  };

  const isCalling = allocation?.callingFlag === 'Calling';
  const isCheckout = allocation?.callingFlag === 'Calling for Checkout';

  /* ─── theme ─── */
  const themeColor = property?.themeColor || '#92400e';
  const darkColor = darkenHex(themeColor, 55);
  const veryDarkColor = darkenHex(themeColor, 90);
  const textColor = isLight(themeColor) ? '#1c1917' : '#ffffff';
  const textMuted = isLight(themeColor) ? 'rgba(28,25,23,0.6)' : 'rgba(255,255,255,0.6)';
  const textFaint = isLight(themeColor) ? 'rgba(28,25,23,0.35)' : 'rgba(255,255,255,0.35)';
  const overlayLight = isLight(themeColor) ? 'rgba(0,0,0,0.08)' : 'rgba(255,255,255,0.12)';
  const logoUrl = property?.logoUrl || '';

  /* ─── loading ─── */
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: themeColor }}>
        <div className="flex flex-col items-center gap-4">
          <div className="w-14 h-14 rounded-full border-4 border-white/30 border-t-white animate-spin" />
          <p className="text-white/70 text-sm font-medium">Loading…</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50 p-6">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-sm w-full text-center">
          <div className="w-16 h-16 bg-rose-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <AlertCircle className="w-8 h-8 text-rose-500" />
          </div>
          <h2 className="text-xl font-bold text-stone-800 mb-2">Not Found</h2>
          <p className="text-stone-500 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* ── Wave animation keyframes ── */}
      <style>{`
        @keyframes waveRing {
          0%   { transform: scale(1);   opacity: 0.7; }
          100% { transform: scale(2.8); opacity: 0; }
        }
        @keyframes btnPress {
          0%   { transform: scale(1); }
          50%  { transform: scale(0.94); }
          100% { transform: scale(1); }
        }
      `}</style>

      {/* ── Toast ── */}
      {toast && (
        <div
          className={`fixed top-5 left-1/2 -translate-x-1/2 z-50 w-[90vw] max-w-xs rounded-2xl px-4 py-3 shadow-xl flex items-center gap-2.5 text-sm font-semibold
            ${toast.type === 'success' ? 'bg-emerald-500 text-white' : toast.type === 'error' ? 'bg-rose-500 text-white' : 'bg-stone-700 text-white'}`}
          data-testid="action-toast"
        >
          <CheckCircle2 className="w-4 h-4 shrink-0" />
          {toast.message}
        </div>
      )}

      {/* ── Full page ── */}
      <div className="min-h-screen w-full flex flex-col">

        {/* ── Full-width logo strip — outside max-w ── */}
        <div className="bg-white shadow-md flex items-center justify-center px-6 py-3" style={{ minHeight: '56px' }}>
          {logoUrl ? (
            <img src={logoUrl} alt="Property logo" className="h-10 w-auto max-w-[180px] object-contain" data-testid="property-logo" />
          ) : (
            <span className="text-base font-bold text-stone-700 tracking-wide">{property?.name || 'SmartFlags'}</span>
          )}
        </div>

        {/* ── Themed content ── */}
        <div className="flex-1 flex flex-col items-center"
          style={{ background: `linear-gradient(160deg, ${themeColor} 0%, ${darkColor} 100%)` }}>
          <div className="w-full max-w-[430px] flex flex-col flex-1 px-6 pt-10 pb-10">

          {/* ── Greeting ── */}
          <div className="text-center flex-1 flex flex-col justify-center">
            <p className="text-xs font-bold uppercase tracking-[0.22em] mb-2" style={{ color: textMuted }}>
              Hello
            </p>
            <h1 className="text-4xl font-extrabold leading-tight mb-3" style={{ color: textColor }} data-testid="guest-name">
              {allocation?.guestName || 'Guest'}
            </h1>
            <p className="text-base" style={{ color: textMuted }} data-testid="property-welcome">
              Welcome to{' '}
              <span className="font-bold" style={{ color: textColor }}>{property?.name || 'Our Resort'}</span>
            </p>

            {/* Room pill */}
            <div className="mt-5 flex justify-center">
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full" style={{ background: overlayLight }}>
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: textColor, opacity: 0.7 }} />
                <span className="text-sm font-semibold" style={{ color: textColor }}>
                  Room {allocation?.roomNumber}
                </span>
                {allocation?.numAdults > 0 && (
                  <span className="text-sm" style={{ color: textFaint }}>
                    · {allocation.numAdults} adult{allocation.numAdults > 1 ? 's' : ''}
                    {allocation.numKids > 0 ? ` + ${allocation.numKids} child${allocation.numKids > 1 ? 'ren' : ''}` : ''}
                  </span>
                )}
              </div>
            </div>
          </div>

          {/* ── Circular 3D Buttons ── */}
          <div className="mt-12 flex justify-center gap-10">

            {/* Call Staff */}
            <div className="flex flex-col items-center gap-3">
              <div className="relative">
                {/* Wave rings when active */}
                {isCalling && <WaveRings color={textColor} />}

                <button
                  onClick={handleCalling}
                  disabled={callingLoading || isCheckout}
                  className="relative w-24 h-24 rounded-full flex items-center justify-center disabled:opacity-50 transition-all"
                  style={{
                    background: isCalling
                      ? `radial-gradient(circle at 35% 35%, #fcd34d, #f59e0b)`
                      : `radial-gradient(circle at 35% 35%, ${overlayLight.replace('0.12', '0.25')}, ${overlayLight})`,
                    boxShadow: isCalling
                      ? `0 6px 0 #b45309, 0 8px 20px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.4)`
                      : `0 6px 0 ${veryDarkColor}, 0 8px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.15)`,
                    border: `2px solid ${isCalling ? 'rgba(251,191,36,0.6)' : 'rgba(255,255,255,0.15)'}`,
                    animation: callingLoading ? 'btnPress 0.3s ease' : undefined,
                  }}
                  data-testid="calling-btn"
                >
                  {callingLoading
                    ? <Loader2 className="w-8 h-8 animate-spin" style={{ color: isCalling ? '#92400e' : textColor }} />
                    : <Bell className="w-8 h-8" style={{ color: isCalling ? '#92400e' : textColor }} />
                  }
                </button>
              </div>
              <span className="text-sm font-semibold" style={{ color: textColor }}>
                {isCalling ? 'Cancel Call' : 'Call Staff'}
              </span>
            </div>

            {/* Request Checkout */}
            <div className="flex flex-col items-center gap-3">
              <div className="relative">
                {isCheckout && <WaveRings color={textColor} />}

                <button
                  onClick={handleCheckout}
                  disabled={checkoutLoading || isCalling}
                  className="relative w-24 h-24 rounded-full flex items-center justify-center disabled:opacity-50 transition-all"
                  style={{
                    background: isCheckout
                      ? `radial-gradient(circle at 35% 35%, #6ee7b7, #10b981)`
                      : `radial-gradient(circle at 35% 35%, ${overlayLight.replace('0.12', '0.25')}, ${overlayLight})`,
                    boxShadow: isCheckout
                      ? `0 6px 0 #065f46, 0 8px 20px rgba(0,0,0,0.35), inset 0 1px 0 rgba(255,255,255,0.4)`
                      : `0 6px 0 ${veryDarkColor}, 0 8px 20px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.15)`,
                    border: `2px solid ${isCheckout ? 'rgba(16,185,129,0.5)' : 'rgba(255,255,255,0.15)'}`,
                    animation: checkoutLoading ? 'btnPress 0.3s ease' : undefined,
                  }}
                  data-testid="checkout-btn"
                >
                  {checkoutLoading
                    ? <Loader2 className="w-8 h-8 animate-spin" style={{ color: isCheckout ? '#064e3b' : textColor }} />
                    : <CreditCard className="w-8 h-8" style={{ color: isCheckout ? '#064e3b' : textColor }} />
                  }
                </button>
              </div>
              <span className="text-sm font-semibold" style={{ color: textColor }}>
                {isCheckout ? 'Cancel' : 'Checkout'}
              </span>
            </div>

          </div>

          {/* Active status strip */}
          {(isCalling || isCheckout) && (
            <div
              className="mt-6 rounded-2xl px-4 py-3 flex items-center gap-2.5 text-sm font-medium"
              style={{ background: overlayLight, color: textColor }}
              data-testid="status-indicator"
            >
              <span className="w-2 h-2 rounded-full shrink-0 animate-pulse" style={{ backgroundColor: isCalling ? '#fcd34d' : '#6ee7b7' }} />
              {isCalling ? 'Staff notified — on their way!' : 'Checkout request active — staff will assist shortly.'}
            </div>
          )}

          {/* ── View Menu ── */}
          <button
            onClick={() => navigate(`/customer/menu/${deviceId}`)}
            className="mt-6 w-full flex items-center justify-center gap-2.5 py-4 rounded-2xl text-base font-bold transition-all active:scale-[0.97]"
            style={{
              background: overlayLight,
              color: textColor,
              boxShadow: `0 4px 0 ${veryDarkColor}, 0 6px 16px rgba(0,0,0,0.25)`,
              border: `1px solid rgba(255,255,255,0.15)`,
            }}
            data-testid="view-menu-btn"
          >
            <UtensilsCrossed className="w-5 h-5" />
            View Menu
          </button>

          <div className="h-6" />
        </div>
        </div>
      </div>
    </>
  );
};

export default CustomerAllocationHome;
